import React, { useEffect, useCallback, useState, useMemo } from 'react';
// import ReactList from 'react-list';
import { confirmAlert } from 'react-confirm-alert'; // Import
import 'react-confirm-alert/src/react-confirm-alert.css';
import { MdAdd } from 'react-icons/md';
import { useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import parse from 'date-fns/parse';
import isAfter from 'date-fns/isAfter';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { Input } from '@rocketseat/unform';
import {
  Column,
  Container,
  ChargeActions,
  PaymentsHeader,
  ClientContainer,
  SubscriptionContainer,
  PaymentsContainer,
  Payments,
  Payment,
  StatusBadge,
  Row,
  ChargeContainer,
  ChargeContent,
  PaymentsInformation,
  PaymentActions,
  NotesContainer,
} from './styles';
import Panel from '~/components/Panel';
import api from '~/services/api';
import { BarLoader } from '~/components';

export default function SubscriptionDetail() {
  const isAdmin = useSelector(state => state.user.profile.isAdmin);
  const { id } = useParams();
  const [subscription, setSubscription] = useState();
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [chargeDeleteLoading, setChargeDeleteLoading] = useState(false);

  const loadSubscription = useCallback(async () => {
    setLoading(true);
    const response = await api.get(`/subscription/${id}`);

    let status;
    if (response.data.status === null) {
      status = 'pending';
    } else if (response.data.status === true) {
      status = 'active';
    } else if (response.data.status === false) {
      status = 'finished';
    }
    const object = { ...response.data, status };
    setSubscription(object);
    setNotes(object.notes || '');
    setLoading(false);
  }, [id]);

  const deleteCharge = useCallback(
    charge => {
      if (!chargeDeleteLoading) {
        setChargeDeleteLoading(true);
        confirmAlert({
          title: 'Are you sure?',
          message: `Delete '${charge.description}' ?`,
          buttons: [
            {
              label: 'Yes',
              onClick: async () => {
                try {
                  await api.delete(`/charge/${charge.id}`);
                } catch (err) {
                  toast.error(
                    err.response &&
                      err.response.data &&
                      err.response.data.error &&
                      typeof err.response.data.error === 'string'
                      ? err.response.data.error
                      : 'Error while deleting charge'
                  );
                }
                await loadSubscription();
                setChargeDeleteLoading(false);
              },
            },
            {
              label: 'No',
              onClick: () => {
                setChargeDeleteLoading(false);
              },
            },
          ],
        });
      }
    },
    [chargeDeleteLoading, loadSubscription]
  );

  const saveNotes = useCallback(async () => {
    if (subscription) {
      try {
        await api.put(`/subscription/${subscription.id}`, { notes });
        toast.success('Notes Updated');
      } catch (err) {
        toast.error(
          err.response &&
            err.response.data &&
            err.response.data.error &&
            typeof err.response.data.error === 'string'
            ? err.response.data.error
            : 'Error while updating notes'
        );
      }
    }
  }, [notes, subscription]);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  const totalAmounts = useMemo(() => {
    let charges = 0;
    let past_due_charges = 0;
    let payments = 0;
    if (subscription) {
      subscription.charges.forEach(charge => {
        charges += Number(charge.amount);
        let currentPastDue = 0;
        const isPastDue =
          !charge.paid &&
          isAfter(new Date(), parse(charge.due_date, 'MM/dd/yyyy', new Date()));
        if (isPastDue) {
          currentPastDue += Number(charge.amount);
        }
        charge.payments.forEach(payment => {
          payments += Number(payment.pivot.used);
          if (isPastDue) {
            currentPastDue -= Number(payment.pivot.used);
          }
        });
        past_due_charges += Number(currentPastDue);
      });
    }
    return [
      `$${charges.toFixed(2)}`,
      `$${payments.toFixed(2)}`,
      `$${Number(charges - payments).toFixed(2)}`,
      `$${Number(past_due_charges || 0).toFixed(2)}`,
    ];
  }, [subscription]);

  const getDueAmount = charge => {
    return (
      Number(charge.amount) -
      Number(
        charge.payments.reduce((total, payment) => {
          return total + Number(payment.pivot.used);
        }, 0)
      )
    ).toFixed(2);
  };
  return loading ? (
    <BarLoader />
  ) : (
    <Container>
      <Row>
        <Panel title="Subscription" flex={3}>
          <SubscriptionContainer>
            <ul>
              <li>ID: {subscription.id}</li>
              {subscription.sales && <li>Sales: {subscription.sales}</li>}
              <li>
                Status:{' '}
                <StatusBadge value={subscription.status}>
                  {subscription.status}
                </StatusBadge>
              </li>
              <li>
                Start: <span>{subscription.start}</span>
              </li>
              <li>
                End: <span>{subscription.end}</span>
              </li>
              <li>
                Term: <span>{subscription.term} months</span>
              </li>
              <li>
                Start Mileage: <span>{subscription.mileage}</span>
              </li>
            </ul>
            {subscription.vehicle && (
              <ul>
                <li>
                  Vehicle: <span>{subscription.vehicle.displayName} </span>
                </li>
                <li>
                  Color: <span>{subscription.vehicle.exteriorColor} </span>
                </li>
                <li>
                  VIN: <span>{subscription.vehicle.vin}</span>
                </li>
                <li>
                  Body Type: <span>{subscription.vehicle.bodyType}</span>
                </li>
                <li>
                  GPS #1: <span>{subscription.vehicle.GPS_1}</span>
                </li>
                <li>
                  GPS #2: <span>{subscription.vehicle.GPS_2}</span>
                </li>
              </ul>
            )}
            <ul>
              <li>
                Activation: <span>${subscription.activation}</span>
              </li>
              <li>
                Activation Tax: <span>${subscription.activationTax}</span>
              </li>
              <li>
                Total Activation: <span>${subscription.totalActivation}</span>
              </li>
              <br />
              <li>
                Surcharge Fee (w/ Tax):{' '}
                <span>${subscription.surchargeFee}</span>
              </li>
              <br />
              <li>
                Monthly: <span>${subscription.monthly}</span>
              </li>
              <li>
                Monthly Tax: <span>${subscription.monthlyTax}</span>
              </li>
              <li>
                Total Monthly: <span>${subscription.totalMonthly}</span>
              </li>
            </ul>
          </SubscriptionContainer>
        </Panel>
        <Column>
          <Panel title="Client" flex={1} style={{ marginLeft: 10 }}>
            <ClientContainer>
              <li>
                Name: <span>{subscription.client.fullName}</span>
              </li>
              <li>
                Email: <span>{subscription.client.email}</span>
              </li>
              <li>
                Phone: <span>{subscription.client.phoneNumber}</span>
              </li>
              <li>
                Address: <span>{subscription.client.fullAddress}</span>
              </li>
            </ClientContainer>
          </Panel>{' '}
          <Panel title="Notes" flex={1} style={{ marginLeft: 10 }}>
            <NotesContainer>
              <Input
                name="notes"
                multiline
                value={notes}
                onChange={event => setNotes(event.target.value)}
              />
              <button type="submit" onClick={saveNotes}>
                Save
              </button>
            </NotesContainer>
          </Panel>
        </Column>
      </Row>
      {/* <Panel title="Notes" flex={1} style={{ marginTop: 10 }} /> */}
      <Panel
        style={{ marginTop: 10 }}
        title={
          <PaymentsHeader>
            Payments
            <div>
              <Link to={`/subscription/${id}/payment/create`}>
                <MdAdd />
                Make Payment
              </Link>
              <Link to={`/subscription/${id}/charge/create`}>
                <MdAdd />
                Add Charge
              </Link>
            </div>
          </PaymentsHeader>
        }
        flex={1}
      >
        <PaymentsContainer>
          <PaymentsInformation>
            <h4>Total Charge: {totalAmounts[0]}</h4>
            <h4>Total Payment: {totalAmounts[1]}</h4>
            <h4>Total Due: {totalAmounts[2]}</h4>
            <h4>Past Due: {totalAmounts[3]}</h4>
          </PaymentsInformation>
          {subscription.charges.map(charge => {
            const chargeTooltipName = `chargeTooltip-${charge.id}`;
            return (
              <ChargeContainer>
                {/* {charge.notes && (
                  <ReactTooltip
                    place="top"
                    type="info"
                    id={chargeTooltipName}
                    effect="float"
                  />
                )} */}
                <ChargeContent
                  data-for={chargeTooltipName}
                  data-tip={charge.notes}
                  paid={charge.paid}
                  remaining={charge.payments.length > 0}
                  pastDue={isAfter(
                    new Date(),
                    parse(charge.due_date, 'MM/dd/yyyy', new Date())
                  )}
                  key={`charge-${charge.id}`}
                >
                  <p>{charge.description}</p>
                  <p>
                    <strong>Amount:</strong> ${charge.amountWithoutTax}
                  </p>
                  <p>
                    <strong>Tax:</strong> ${charge.tax}
                  </p>
                  <p>
                    <strong>Total:</strong> ${charge.amount}
                  </p>
                  <p>
                    <strong>Due Date:</strong> {charge.due_date}
                  </p>
                  <p>
                    <strong>Payments done:</strong> {charge.payments.length}
                  </p>
                  {charge.paid && (
                    <p>
                      <strong>Paid at:</strong> {charge.paid_at}
                    </p>
                  )}
                  {!charge.paid && charge.payments.length > 0 && (
                    <p>
                      <strong>Amount Due: </strong> ${getDueAmount(charge)}
                    </p>
                  )}
                  {charge.notes && (
                    <p>
                      Notes: <strong>({charge.notes})</strong>
                    </p>
                  )}
                  <ChargeActions>
                    {!charge.paid && (
                      <Link
                        to={{
                          pathname: `/subscription/${id}/payment/create`,
                          state: {
                            chargeId: charge.id,
                            chargeName: charge.description,
                          },
                        }}
                      >
                        <MdAdd /> Payment
                      </Link>
                    )}
                    {isAdmin && (
                      <Link
                        to={{
                          pathname: `/subscription/${id}/charge/edit`,
                          state: {
                            item: charge,
                          },
                        }}
                      >
                        <FaEdit />
                      </Link>
                    )}
                    {isAdmin && charge.payments.length === 0 && (
                      <button
                        type="button"
                        disabled={chargeDeleteLoading}
                        onClick={() => deleteCharge(charge)}
                      >
                        <FaTrash />
                      </button>
                    )}
                  </ChargeActions>
                </ChargeContent>

                <Payments>
                  {charge.payments.map(payment => {
                    const paymentTooltipName = `paymentTooltip-${payment.id}`;
                    return (
                      <>
                        {/* {payment.notes && (
                          <ReactTooltip
                            place="top"
                            type="info"
                            id={paymentTooltipName}
                            effect="float"
                          />
                        )} */}
                        <Payment
                          data-for={paymentTooltipName}
                          data-tip={payment.notes}
                          key={`payment-${payment.id}`}
                        >
                          <p>
                            <strong>Payment #</strong>
                            {payment.id}
                          </p>
                          <p>
                            <strong>Amount Used: </strong> ${payment.pivot.used}
                          </p>
                          <p>
                            <strong>Total Amount:</strong> ${payment.amount}
                          </p>
                          <p>
                            <strong>Type:</strong> {payment.type}
                            <br />
                            {payment.notes && (
                              <strong>({payment.notes})</strong>
                            )}
                          </p>
                          <p>
                            <strong>Date:</strong> {payment.date}
                          </p>
                          <PaymentActions>
                            {isAdmin && (
                              <Link
                                to={{
                                  pathname: `/subscription/${id}/payment/edit`,
                                  state: {
                                    item: payment,
                                  },
                                }}
                              >
                                <FaEdit />
                              </Link>
                            )}
                          </PaymentActions>
                        </Payment>
                      </>
                    );
                  })}
                </Payments>
              </ChargeContainer>
            );
          })}
        </PaymentsContainer>
      </Panel>
    </Container>
  );
}
